import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Send, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Paywall from "../components/Paywall";

const SUGGESTED = [
  "How am I doing this month?",
  "Where can I cut back?",
  "Am I saving enough for my goals?",
  "What budget should I set?",
];

export default function Coach() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/coach/messages");
    setMessages(data);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/coach/chat", { content });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      toast.error("Coach unavailable. Try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Clear all coach messages?")) return;
    await api.delete("/coach/messages");
    setMessages([]);
    toast.success("Conversation cleared");
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="coach-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">AI / TERMINAL</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter flex items-center gap-3">
            Coach
            <Sparkles size={28} className="text-[var(--action-blue)]" />
          </h1>
          <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">
            Ask anything about your spending, budgets, or savings. The coach sees your live data.
          </p>
        </div>
        {user?.is_premium && (
          <button
            data-testid="coach-reset"
            onClick={reset}
            className="border-2 border-black px-3 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors flex items-center gap-2 w-fit"
          >
            <RotateCcw size={14} /> CLEAR
          </button>
        )}
      </div>

      {!user?.is_premium && (
        <Paywall
          title="Coach is a Premium feature"
          description="Chat with an AI financial coach that knows your income, budgets, expenses, and goals. One-time payment, lifetime access."
        />
      )}

      {user?.is_premium && (
      <>
      {/* Chat area */}
      <div className="bg-[#111111] border border-black p-1">
        <div
          ref={scrollRef}
          data-testid="coach-messages"
          className="h-[500px] overflow-y-auto p-5 space-y-4 font-mono text-sm"
        >
          {messages.length === 0 && !sending && (
            <div className="text-white/60">
              <div className="overline text-white/40 mb-2">SUGGESTED PROMPTS</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    data-testid={`suggested-${s.slice(0, 8)}`}
                    onClick={() => send(s)}
                    className="text-left border border-white/30 p-3 text-white hover:bg-white hover:text-black transition-colors"
                  >
                    → {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={`${m.created_at}-${i}`} data-testid={`msg-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={`max-w-[80%] px-4 py-3 ${
                  m.role === "user"
                    ? "bg-white text-black border border-white"
                    : "bg-transparent text-white border border-white/30"
                }`}
              >
                <div className="overline mb-1" style={{ color: m.role === "user" ? "#666" : "#FF2B00" }}>
                  {m.role === "user" ? "YOU" : "█ COACH"}
                </div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="border border-white/30 px-4 py-3 text-white/70">
                <span className="text-[var(--alert-red)] blink">█</span> thinking…
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="flex gap-2"
        data-testid="coach-form"
      >
        <input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the coach…"
          className="flex-1 bg-white border-2 border-black px-4 py-3 font-mono outline-none focus:border-[var(--action-blue)]"
        />
        <button
          data-testid="coach-send"
          type="submit"
          disabled={sending || !input.trim()}
          className="bg-black text-white px-5 py-3 font-display font-bold hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Send size={16} /> SEND
        </button>
      </form>
      </>
      )}
    </div>
  );
}
